import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatMoney, formatDate } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MagnifyingGlass, Gavel, Warning, Clock, CalendarBlank } from '@phosphor-icons/react';

const HEARING_STYLES = {
  overdue: { label: 'جلسة فائتة', bg: 'bg-red-100', text: 'text-red-700', icon: Warning },
  imminent: { label: 'خلال 7 أيام', bg: 'bg-orange-100', text: 'text-orange-700', icon: Clock },
  scheduled: { label: 'مجدولة', bg: 'bg-blue-100', text: 'text-blue-700', icon: CalendarBlank },
  none: { label: 'لا يوجد', bg: 'bg-slate-100', text: 'text-slate-500', icon: CalendarBlank },
};

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  under_review: 'bg-blue-100 text-blue-700',
  filed: 'bg-indigo-100 text-indigo-700',
  in_hearing: 'bg-orange-100 text-orange-700',
  judgment: 'bg-purple-100 text-purple-700',
  execution: 'bg-violet-100 text-violet-700',
  closed: 'bg-emerald-100 text-emerald-700',
  settled: 'bg-teal-100 text-teal-700',
};

export default function LegalCases() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ statuses: [] });
  const [q, setQ] = useState('');
  const [flag, setFlag] = useState('all');

  useEffect(() => {
    api.get('/legal/cases').then(({ data }) => setItems(data)).finally(() => setLoading(false));
    api.get('/legal/settings').then(({ data }) => setSettings(data));
  }, []);

  const statusLabel = (code) => settings.statuses.find(s => s.code === code)?.label_ar || code;

  const summary = useMemo(() => ({
    total: items.length,
    overdue: items.filter(i => i.hearing_flag === 'overdue').length,
    imminent: items.filter(i => i.hearing_flag === 'imminent').length,
    scheduled: items.filter(i => i.hearing_flag === 'scheduled').length,
  }), [items]);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (flag !== 'all' && i.hearing_flag !== flag) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (i.claim_no || '').toLowerCase().includes(s) ||
             (i.case_no || '').toLowerCase().includes(s) ||
             (i.tenant_name || '').toLowerCase().includes(s) ||
             (i.court_name || '').toLowerCase().includes(s);
    });
  }, [items, q, flag]);

  return (
    <div className="space-y-6" data-testid="legal-cases-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#1E3A5F] mb-1">Judicial Cases Follow-up</div>
        <h1 className="text-3xl font-bold text-slate-900">متابعة القضايا</h1>
        <p className="text-sm text-slate-500 mt-1">القضايا التي تم رفعها للمحاكم مع تنبيه الجلسات القريبة</p>
      </div>

      {/* Alerts summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border border-slate-200 rounded-md shadow-none cursor-pointer hover:border-slate-900" onClick={() => setFlag('all')} data-testid="tile-total">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">إجمالي القضايا</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{summary.total}</div>
            </div>
            <Gavel className="text-[#1E3A5F]" size={26} weight="duotone" />
          </div>
        </Card>
        <Card className="p-4 border border-red-200 bg-red-50/40 rounded-md shadow-none cursor-pointer hover:border-red-500" onClick={() => setFlag('overdue')} data-testid="tile-overdue">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-red-600">جلسات فائتة</div>
              <div className="text-2xl font-bold text-red-700 tabular-nums">{summary.overdue}</div>
            </div>
            <Warning className="text-red-600" size={26} weight="duotone" />
          </div>
        </Card>
        <Card className="p-4 border border-orange-200 bg-orange-50/40 rounded-md shadow-none cursor-pointer hover:border-orange-500" onClick={() => setFlag('imminent')} data-testid="tile-imminent">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-orange-700">قريبة (خلال 7 أيام)</div>
              <div className="text-2xl font-bold text-orange-700 tabular-nums">{summary.imminent}</div>
            </div>
            <Clock className="text-orange-600" size={26} weight="duotone" />
          </div>
        </Card>
        <Card className="p-4 border border-blue-200 bg-blue-50/30 rounded-md shadow-none cursor-pointer hover:border-blue-500" onClick={() => setFlag('scheduled')} data-testid="tile-scheduled">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-blue-700">مجدولة</div>
              <div className="text-2xl font-bold text-blue-700 tabular-nums">{summary.scheduled}</div>
            </div>
            <CalendarBlank className="text-blue-600" size={26} weight="duotone" />
          </div>
        </Card>
      </div>

      {/* Filter bar */}
      <Card className="p-4 border border-slate-200 rounded-md shadow-none">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 mb-1 block">بحث</label>
            <div className="relative">
              <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pr-9 h-9" placeholder="رقم القضية، الدعوى، المستأجر، المحكمة..." data-testid="cases-search" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">حالة الجلسة</label>
            <Select value={flag} onValueChange={setFlag}>
              <SelectTrigger className="h-9" data-testid="cases-flag-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                <SelectItem value="overdue">فائتة</SelectItem>
                <SelectItem value="imminent">قريبة (7 أيام)</SelectItem>
                <SelectItem value="scheduled">مجدولة</SelectItem>
                <SelectItem value="none">بلا جلسة</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Cases table */}
      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="p-3 text-start">رقم المطالبة</th>
                <th className="p-3 text-start">رقم الدعوى</th>
                <th className="p-3 text-start">المحكمة</th>
                <th className="p-3 text-start">المستأجر</th>
                <th className="p-3 text-start">الفرع</th>
                <th className="p-3 text-end">المطالب به</th>
                <th className="p-3 text-start">الحالة</th>
                <th className="p-3 text-start">الجلسة القادمة</th>
                <th className="p-3 text-start">الموظف</th>
                <th className="p-3 text-start">المحامي</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-8 text-center text-slate-400">جارٍ التحميل...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-12 text-center text-slate-400">
                  <Gavel className="mx-auto mb-2 text-slate-300" size={32} />
                  لا توجد قضايا مطابقة
                </td></tr>
              ) : filtered.map(c => {
                const hf = HEARING_STYLES[c.hearing_flag] || HEARING_STYLES.none;
                const HFIcon = hf.icon;
                const isOverdue = c.hearing_flag === 'overdue';
                const isImminent = c.hearing_flag === 'imminent';
                return (
                  <tr
                    key={c.id}
                    onClick={() => nav(`/legal/claims/${c.id}`)}
                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${isOverdue ? 'bg-red-50/30' : isImminent ? 'bg-orange-50/30' : ''}`}
                    data-testid={`case-row-${c.claim_no}`}
                  >
                    <td className="p-3 font-mono font-semibold text-slate-900">{c.claim_no}</td>
                    <td className="p-3 font-mono text-slate-800">{c.case_no || '—'}</td>
                    <td className="p-3">{c.court_name || '—'}</td>
                    <td className="p-3">{c.tenant_name || '—'}</td>
                    <td className="p-3">{c.branch || '—'}</td>
                    <td className="p-3 text-end tabular-nums font-semibold">{formatMoney(c.claim_amount)}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLES[c.status] || 'bg-slate-100'}`}>{statusLabel(c.status)}</span>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ${hf.bg} ${hf.text}`}>
                        <HFIcon size={12} weight="bold" />
                        {c.next_hearing_date ? formatDate(c.next_hearing_date) : hf.label}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{c.assigned_to_name || '—'}</td>
                    <td className="p-3 text-slate-600">{c.lawyer_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
